import {
  BASE_AVATARES,
  MANIFIESTO_VACIO,
  parsearManifiesto,
  type ManifiestoAvatares,
} from './manifiesto';

/** Manifiesto de producción, versionado en `public/avatars/`. */
export const URL_MANIFIESTO = `${BASE_AVATARES}manifest.json`;

/**
 * Manifiesto local de desarrollo, ignorado por git. Puede referenciar el
 * avatar de `default/`, que el de producción no puede (D-4).
 */
export const URL_MANIFIESTO_LOCAL = `${BASE_AVATARES}manifest.local.json`;

export interface OpcionesCarga {
  fetch: typeof fetch;
  /** `import.meta.env.DEV`. Sólo en desarrollo se pide el manifiesto local. */
  dev: boolean;
  /** Último recurso si el manifiesto no llega o no valida (D-3). */
  compilado: ManifiestoAvatares;
  avisar?: (mensaje: string) => void;
}

/**
 * Obtiene el manifiesto de avatares. **Nunca falla**: la sesión funciona
 * sin avatar (D-9), así que un manifiesto ausente o inválido degrada al
 * compilado, y éste puede estar vacío.
 *
 * Orden: el local (sólo en desarrollo), el de producción, el compilado.
 */
export async function cargarManifiesto(op: OpcionesCarga): Promise<ManifiestoAvatares> {
  const avisar = op.avisar ?? ((m: string) => console.warn(m));

  if (op.dev) {
    // En desarrollo el local es opcional: si no existe, el dev server de
    // Vite responde el `index.html` (fallback de SPA) y el JSON no parsea.
    // Se ignora en silencio y se sigue con el de producción.
    const local = await leerJson(op.fetch, URL_MANIFIESTO_LOCAL);
    if (local.ok) {
      const m = parsearManifiesto(local.valor, URL_MANIFIESTO_LOCAL, avisar);
      if (m) return m;
    }
  }

  const remoto = await leerJson(op.fetch, URL_MANIFIESTO);
  if (remoto.ok) {
    const m = parsearManifiesto(remoto.valor, URL_MANIFIESTO, avisar);
    if (m) return m;
  } else {
    avisar(`[avatar] no se pudo leer ${URL_MANIFIESTO} (${remoto.motivo})`);
  }
  avisar('[avatar] se usa el manifiesto compilado en el build');
  return op.compilado;
}

type Lectura = { ok: true; valor: unknown } | { ok: false; motivo: string };

async function leerJson(f: typeof fetch, url: string): Promise<Lectura> {
  try {
    // `no-cache` revalida contra el servidor: un manifiesto nuevo se ve al
    // recargar sin esperar a que venza el caché (D-8).
    const res = await f(url, { cache: 'no-cache' });
    if (!res.ok) return { ok: false, motivo: `HTTP ${res.status}` };
    return { ok: true, valor: await res.json() };
  } catch (err) {
    return { ok: false, motivo: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * El manifiesto de producción tal como estaba al compilar. El plugin de
 * Vite (`vite-plugin-avatares.ts`) lo inyecta en `__MANIFIESTO_COMPILADO__`;
 * fuera de Vite (tests) no existe y queda el vacío.
 */
export function manifiestoCompilado(): ManifiestoAvatares {
  if (typeof __MANIFIESTO_COMPILADO__ === 'undefined') return MANIFIESTO_VACIO;
  return parsearManifiesto(__MANIFIESTO_COMPILADO__, 'manifiesto compilado') ?? MANIFIESTO_VACIO;
}
